import { useState } from "react";
import { FaRegArrowAltCircleRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import Button from "@Components/Button";
import Circle from "@Components/Circle";
import StatusSwitcher from "@Components/LineSwitch/LineSwitch";
import Loading from "@Components/Loading";
import { useGetInfiniteTickets } from "@Hooks/useTickets";
import LatestLoans from "@Pages/Applications/LatestLoans";
import { priorityToColor, ticketStatusToColor } from "@Pages/Tickets/Tickets";
import TicketsChat from "@Pages/Tickets/TicketsChat/TicketsChat";
import { LenderTicket, sortTickets, Status } from "@Types/tickets";
import { capitalize } from "@Utils/functions";

import styles from "./LatestTickets.module.scss";
import TicketsByStatus from "./TicketsByStatus";

export type ExtendedStatus = Status | "All";

//TO-DO Quickfix, should be on db level
export const solvedToResolved = (status: Status) => {
  if (status === Status.Sovled) return "Resolved";
  else return status;
};
const RenderTicket = ({ ticket }: { ticket: LenderTicket }) => {
  const navigate = useNavigate();

  const redirectToTicket = () => navigate(`/tickets/${ticket._id}`);

  return (
    <div className={styles.titleIssue}>
      <div className={styles.textInfo}>Status</div>
      <div
        className={styles.statusValue}
        style={{ background: ticketStatusToColor(ticket.data.info.status) }}
      >
        {capitalize(solvedToResolved(ticket?.data?.info.status)) ?? ""}
      </div>
      <div className={styles.textInfo}>Subject</div>
      <div className={styles.textValue}>{ticket.data.info.subject ?? " "}</div>
      <div className={styles.textInfo}>Priority</div>
      <div className={styles.textValue}>
        <Circle size={15} color={priorityToColor(ticket.data.info.priority)} />
        {capitalize(ticket.data.info.priority)}
      </div>
      <div className={styles.textInfo}>Email</div>
      <div className={styles.textValue}>{ticket.data.replyTo?.join(" / ")}</div>
      <div className={styles.textInfo}>Description</div>
      <div className={styles.textValue}>
        {ticket.data.info.description ?? ""}
      </div>
      <div className={styles.seeDetails} onClick={redirectToTicket}>
        <div>See details</div>
        <FaRegArrowAltCircleRight size={24} />
      </div>
    </div>
  );
};

function LatestTitleIssues({
  dashboardSelect,
}: {
  dashboardSelect: JSX.Element | null;
}) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useGetInfiniteTickets({
      options: {
        limit: 6,
        sort: { createdAt: "desc" },
        page: 1,
        pagination: true,
      },
    });

  const [selectedOption, setSelectedOption] = useState<ExtendedStatus>("All");
  const flattenedPages = data?.pages?.flatMap((group) => group) ?? [];
  const tickets = flattenedPages?.flatMap((x) => x?.docs?.flat());

  const sortedTickets = sortTickets(tickets ?? []);

  const options = [
    { label: "All" as ExtendedStatus, value: sortedTickets.all },
    { label: Status.Open, value: sortedTickets.open },
    { label: Status.InProgress, value: sortedTickets["in progress"] },
    { label: "Resolved" as ExtendedStatus, value: sortedTickets.solved },
    { label: Status.Closed, value: sortedTickets.closed },
  ];
  const handleChange = (option: ExtendedStatus) => setSelectedOption(option);

  return (
    <div className={styles.container}>
      <div className={styles.dashboardSelect}>
        <div>{dashboardSelect}</div>
      </div>

      <div className={styles.chartContainer}>
        <div className={styles.titleContainer}>
          <LatestLoans />
        </div>
        <div className={styles.chart}>
          <TicketsByStatus />
        </div>
      </div>
      <div className={styles.header}>
        <h2 className={styles.title}>Latest Titles Tickets</h2>
        <TicketsChat />
      </div>
      <StatusSwitcher<ExtendedStatus>
        options={options}
        handleChange={handleChange}
        selectedOption={selectedOption}
      />
      <div className={styles.titleIssueContainer}>
        {tickets && tickets?.length > 0 ? (
          tickets
            .filter(
              (ticket) =>
                selectedOption === "All" ||
                ticket.data.info.status === selectedOption
            )
            .map((ticket) => <RenderTicket ticket={ticket} key={ticket._id} />)
        ) : isFetching ? (
          <div>Loading...</div>
        ) : null}
      </div>
      <div className={styles.fetchMore}>
        {isFetchingNextPage ? (
          <Loading />
        ) : hasNextPage ? (
          <Button variant="blue" onClick={() => fetchNextPage()}>
            Load more
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default LatestTitleIssues;
